<template>
    <AdminSelect
        v-model="modelValue"
        :options="options"
        :dusk="props.dusk"
        :size="props.size"
        :multiple="props.multiple"
        :searchable="true"
        :tabindex="props.tabindex"
        @change="emit('change', $event)"
        @search="search"
    >
        <template #currentValue="slotProps">
            <slot name="currentValue" v-bind="slotProps"></slot>
        </template>
        <template #option="slotProps">
            <slot name="option" v-bind="slotProps"></slot>
        </template>
        <template #optionsBefore="slotProps">
            <slot name="optionsBefore" v-bind="slotProps"></slot>
        </template>
        <template #optionsAfter="slotProps">
            <slot name="optionsAfter" v-bind="slotProps"></slot>
        </template>
    </AdminSelect>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';

type SelectedValue = null | string | number | object | string[] | number[] | object[];

const emit = defineEmits<{
    (event: 'update:modelValue', content: SelectedValue): void;
    (event: 'change', content: SelectedValue): void;
}>();

const modelValue = defineModel({
    type: [String, Number, Object, Array],
});

const props = withDefaults(defineProps<FieldComponent>(), {
    modelValue: null,
    options: () => [],
    placeholder: undefined,
    property: 'id',
    size: 'base',
    dusk: null,
    label: 'name',
    opened: false,
    valid: false,
    invalid: false,
    variant: null,
    disabled: false,
    multiple: false,
    removable: true,
    tabindex: null,
    ajaxRoute: null,
    preload: false,
    create: false,
    loadCurrent: true,
});

const options = ref(props.options);

const ajaxRoute = computed(() => props.field.route ?? props.ajaxRoute);

/**
 * Search data
 */
const search = (value: string) => {
    axios
        .get(route(ajaxRoute.value), {
            params: { search: value },
        })
        .then(({ data }) => {
            options.value = data.data;
        });
};

/**
 * Preload data
 */
const all = () => {
    axios.get(route(ajaxRoute.value)).then(({ data }) => {
        options.value = data.data;
    });
};

if (props.preload) {
    all();
}

/**
 * Load current value
 */
const loadCurrent = () => {
    if (props.loadCurrent && modelValue.value) {
        axios.get(route(ajaxRoute.value, { id: modelValue.value })).then(({ data }) => {
            options.value = data;
        });
    }
};

if (props.loadCurrent) {
    loadCurrent();
}

/**
 * Reload field
 */
const reload = () => {
    all();
    loadCurrent();
};

defineExpose({ reload });
</script>
